import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listAdminSpots, createSpot, updateSpot, setSpotActive } from '../../api/admin';
import { SpotFormDialog } from './SpotFormDialog';
import type { AdminSpotResponse } from '../../types/api';

export function AdminSpotsTab() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AdminSpotResponse | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-spots'],
    queryFn: listAdminSpots,
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) => setSpotActive(id, active),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-spots'] }),
  });

  function openNew() {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(spot: AdminSpotResponse) {
    setEditing(spot);
    setDialogOpen(true);
  }

  async function handleSave(name: string, latitude: number, longitude: number) {
    if (editing) {
      await updateSpot(editing.id, { name, latitude, longitude });
    } else {
      await createSpot({ name, latitude, longitude });
    }
    queryClient.invalidateQueries({ queryKey: ['admin-spots'] });
  }

  if (isLoading) return <div className="p-4">Loading spots…</div>;
  if (error) return <div className="p-4 text-red-500">Failed to load spots.</div>;

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button
          type="button"
          onClick={openNew}
          className="rounded-md bg-teal-600 hover:bg-teal-700 px-4 py-2 text-sm font-medium text-white"
        >
          New Spot
        </button>
      </div>

      {!data || data.length === 0 ? (
        <div className="p-4 text-slate-500">No spots.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
              <tr>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Latitude</th>
                <th className="px-3 py-2">Longitude</th>
                <th className="px-3 py-2">Catches</th>
                <th className="px-3 py-2">Active</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {data.map(spot => (
                <tr key={spot.id} className={spot.active ? '' : 'opacity-50'}>
                  <td className="px-3 py-2 font-medium text-slate-900 dark:text-slate-100">{spot.name}</td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{spot.latitude}</td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{spot.longitude}</td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{spot.catchCount}</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => toggleActive.mutate({ id: spot.id, active: !spot.active })}
                      className={`rounded px-2 py-0.5 text-xs font-medium ${
                        spot.active
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
                      }`}
                    >
                      {spot.active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => openEdit(spot)}
                      className="text-xs text-teal-600 hover:underline dark:text-teal-400"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SpotFormDialog
        open={dialogOpen}
        spot={editing}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
      />
    </div>
  );
}
